import React from 'react'
import { Navigate, Route } from 'react-router'

import { hasOnlinePaymentSectionEnabled } from '@/resources/services/permission/permissions'

import AuthenticatedRoute from './authenticated'
import SplitControl from '@/components/business/SplitControl'
import SplitHandlerScheduledOrders from '@/pages/ScheduledOrders/components/SplitHandler'
import SplitHandlerOnSales from '@/pages/OnSale/components/SplitHandler'
import OrderHistoryStoreProvider from '@/contexts/orderHistory'
import InventoryStoreProvider from '@/contexts/inventory'
import { AutomaticPrintProvider } from '@/contexts/automaticPrint'
import ActiveOrdersNanodropProvider from '@/contexts/activeOrdersNanodrop'
import { OrdersProvider } from '@/contexts/orders'
import { CouponProvider } from '@/contexts/coupon/useCouponContext'

const DatesAvailability = React.lazy(
  () =>
    import(
      '../pages/DatesAvailability' /* webpackChunkName: 'DatesAvailability' */
    )
)

const Management = React.lazy(
  () => import('../pages/Management' /* webpackChunkName: 'Management' */)
)

const Performance = React.lazy(
  () => import('../pages/Performance' /* webpackChunkName: 'Performance' */)
)

const TenMinutesReleaseRate = React.lazy(
  () =>
    import(
      '../pages/Performance/subPages/TenMinutesReleaseRate' /* webpackChunkName: 'TenMinutesReleaseRate' */
    )
)

const Inventory = React.lazy(
  () => import('../pages/Inventory' /* webpackChunkName: 'inventory' */)
)

const SearchedProducts = React.lazy(
  () =>
    import(
      '../pages/Inventory/subpages/SearchedProducts' /* webpackChunkName: 'mostSearchedProducts' */
    )
)

const Orders = React.lazy(
  () => import('../pages/Orders' /* webpackChunkName: 'orders' */)
)

const Order = React.lazy(
  () => import('../pages/Order' /* webpackChunkName: 'order' */)
)

const OrdersHistory = React.lazy(
  () => import('../pages/OrdersHistory' /* webpackChunkName: 'orderHistory' */)
)

const FleetManagement = React.lazy(
  () =>
    import('../pages/FleetManagement' /* webpackChunkName: 'fleetManagement' */)
)

const MyFleet = React.lazy(
  () =>
    import(
      '../pages/FleetManagement/subpages/MyFleet' /* webpackChunkName: 'myFleet' */
    )
)

const NoDeliverymanLinked = React.lazy(
  () =>
    import(
      '../pages/FleetManagement/subpages/NoDeliverymanLinked' /* webpackChunkName: 'noDeliverymanLinked' */
    )
)
const MyAccount = React.lazy(
  () => import('../pages/MyAccount' /* webpackChunkName: 'myAccount' */)
)

const DeliverymanApproval = React.lazy(
  () =>
    import(
      '../pages/MyAccount/subpages/DeliverymanApproval' /* webpackChunkName: 'deliverymanApproval' */
    )
)

const DeliverymanManager = React.lazy(
  () =>
    import(
      '../pages/MyAccount/subpages/DeliverymanManager' /* webpackChunkName: 'deliverymanManager' */
    )
)

const PartnerBankDetails = React.lazy(
  () =>
    import(
      '../pages/MyAccount/subpages/PartnerBankDetails' /* webpackChunkName: 'partnerBankDetails' */
    )
)

const EditPocInfo = React.lazy(
  () =>
    import(
      '../pages/MyAccount/subpages/EditPocInfo' /* webpackChunkName: 'editPocInfo' */
    )
)

const OnSale = React.lazy(
  () => import('../pages/OnSale' /* webpackChunkName: 'OnSale' */)
)

const OnlinePayment = React.lazy(
  () => import('../pages/OnlinePayment' /* webpackChunkName: 'onlinePayment' */)
)

const NotFound = React.lazy(
  () => import('../pages/NotFound' /* webpackChunkName: 'notFound' */)
)

const Maintenance = React.lazy(
  () => import('../pages/Maintenance' /* webpackChunkName: 'maintenance' */)
)

const OrdersMap = React.lazy(
  () => import('../pages/OrdersMap' /* webpackChunkName: 'ordersMap' */)
)

const DynamicRadius = React.lazy(
  () => import('../pages/DynamicRadius' /* webpackChunkName: 'dynamicRadius' */)
)

const FinancialReport = React.lazy(
  () => import('../pages/FinancialReport' /* webChunkName: 'financialReport' */)
)

const ScheduledOrders = React.lazy(
  () => import('../pages/ScheduledOrders' /* webChunkName: 'scheduledOrders' */)
)

const Home = React.lazy(
  () => import('../pages/Home' /* webChunkName: 'home' */)
)

const Coupon = React.lazy(
  () => import('../pages/Coupon' /* webChunkName: 'coupon' */)
)

const Coupons = React.lazy(
  () => import('../pages/Coupons' /* webChunkName: 'coupons' */)
)

const RestitutionDetails = React.lazy(
  () =>
    import(
      '../pages/RestitutionDetails' /* webChunkName: 'restitutionDetails' */
    )
)

const OrdersNanoDrop = React.lazy(
  () => import('../pages/NanoDrop' /* webpackChunkName: 'ordersNanoDrop' */)
)

const OnlinePaymentRoute = () => {
  if (hasOnlinePaymentSectionEnabled()) {
    return (
      <AuthenticatedRoute>
        <OnlinePayment />
      </AuthenticatedRoute>
    )
  }
  return <Navigate to="/poc-orders" />
}

export default (
  <>
    <Route
      path="poc-orders"
      element={
        <AuthenticatedRoute>
          <InventoryStoreProvider>
            <OrdersProvider>
              <AutomaticPrintProvider>
                <Orders />
              </AutomaticPrintProvider>
            </OrdersProvider>
          </InventoryStoreProvider>
        </AuthenticatedRoute>
      }
    />
    <Route
      path="coupons"
      element={
        <AuthenticatedRoute>
          <Coupons />
        </AuthenticatedRoute>
      }
    />
    <Route
      path="history"
      element={
        <AuthenticatedRoute>
          <OrderHistoryStoreProvider>
            <OrdersHistory />
          </OrderHistoryStoreProvider>
        </AuthenticatedRoute>
      }
    />
    <Route
      path="order/:id"
      element={
        <AuthenticatedRoute>
          <InventoryStoreProvider>
            <Order />
          </InventoryStoreProvider>
        </AuthenticatedRoute>
      }
    />
    <Route
      path="create-coupon"
      element={
        <AuthenticatedRoute>
          <CouponProvider>
            <Coupon />
          </CouponProvider>
        </AuthenticatedRoute>
      }
    />
    <Route
      path="coupon/:id"
      element={
        <AuthenticatedRoute>
          <CouponProvider>
            <Coupon />
          </CouponProvider>
        </AuthenticatedRoute>
      }
    />

    <Route path="inventory" element={<AuthenticatedRoute />}>
      <Route
        index
        element={
          <InventoryStoreProvider>
            <Inventory />
          </InventoryStoreProvider>
        }
      />
      <Route path="searched-products" element={<SearchedProducts />} />
    </Route>
    <Route path="fleet-management" element={<AuthenticatedRoute />}>
      <Route index element={<FleetManagement />} />
      <Route path="my-fleet" element={<MyFleet />} />
      <Route path="no-deliveryman-linked" element={<NoDeliverymanLinked />} />
    </Route>
    <Route path="my-account" element={<AuthenticatedRoute />}>
      <Route index element={<MyAccount />} />
      <Route path="deliveryman-approval" element={<DeliverymanApproval />} />
      <Route path="deliveryman-manager" element={<DeliverymanManager />} />
      <Route path="partner-bank-details" element={<PartnerBankDetails />} />
      <Route path="edit-poc-info" element={<EditPocInfo />} />
    </Route>
    <Route path="performance" element={<AuthenticatedRoute />}>
      <Route index element={<Performance />} />
      <Route
        path="ten-minutes-delivery-release-rate"
        element={<TenMinutesReleaseRate />}
      />
    </Route>
    <Route
      path="management"
      element={
        <AuthenticatedRoute>
          <Management />
        </AuthenticatedRoute>
      }
    />
    <Route
      path="dates-availability"
      element={
        <AuthenticatedRoute>
          <DatesAvailability />
        </AuthenticatedRoute>
      }
    />
    <Route
      path="orders-map"
      element={
        <AuthenticatedRoute>
          <OrdersMap />
        </AuthenticatedRoute>
      }
    />
    <Route
      path="on-sale"
      element={
        <AuthenticatedRoute>
          <InventoryStoreProvider>
            <SplitHandlerOnSales>
              <OnSale />
            </SplitHandlerOnSales>
          </InventoryStoreProvider>
        </AuthenticatedRoute>
      }
    />
    <Route path="online-payment" element={<OnlinePaymentRoute />} />
    <Route
      path="maintenance"
      element={
        <AuthenticatedRoute>
          <Maintenance />
        </AuthenticatedRoute>
      }
    />
    <Route
      path="dynamic-radius"
      element={
        <AuthenticatedRoute>
          <DynamicRadius />
        </AuthenticatedRoute>
      }
    />
    <Route
      path="financial-report"
      element={
        <AuthenticatedRoute>
          <FinancialReport />
        </AuthenticatedRoute>
      }
    />
    <Route
      path="home"
      element={
        <AuthenticatedRoute>
          <SplitControl
            splitName="new_home_page"
            treatmentOff={
              <Navigate to="/poc-orders" state={{ previousPage: '/login' }} />
            }
          >
            <Home />
          </SplitControl>
        </AuthenticatedRoute>
      }
    />
    <Route path="/" element={<Navigate to="/home" />} />
    <Route
      path="poc-orders-scheduled"
      element={
        <AuthenticatedRoute>
          <SplitHandlerScheduledOrders>
            <ScheduledOrders />
          </SplitHandlerScheduledOrders>
        </AuthenticatedRoute>
      }
    />
    <Route
      path="restitution-details/:yearWeek"
      element={
        <AuthenticatedRoute>
          <RestitutionDetails />
        </AuthenticatedRoute>
      }
    />
    <Route
      path="nano-drop"
      element={
        <AuthenticatedRoute>
          <SplitControl
            splitName="seller_nanodrop_enable"
            treatmentOff={<Navigate to="/poc-orders" replace />}
          >
            <ActiveOrdersNanodropProvider>
              <AutomaticPrintProvider>
                <OrdersNanoDrop />
              </AutomaticPrintProvider>
            </ActiveOrdersNanodropProvider>
          </SplitControl>
        </AuthenticatedRoute>
      }
    />
    <Route
      path="*"
      element={
        <AuthenticatedRoute>
          <NotFound />
        </AuthenticatedRoute>
      }
    />
  </>
)
